import type { ReactNode } from 'react';
import React, { useMemo, useState } from 'react';
import type {
  PaymentBookingResume,
  PaymentErrorMessageParams,
  PaymentRedirectMutationParamsExtended,
  TrackPayementFunction,
} from 'Src/types/payment';

export type PaymentInfoContextType = {
  currency?: string;
  donationAmount?: number | '';
  bookingStatus?: string;
  bookingResume: PaymentBookingResume;
  redirectUri?: string | null;
  onPaymentProgress?: (location: URI) => void;
  paymentRedirectMutation: (params: PaymentRedirectMutationParamsExtended) => Promise<unknown>;
  trackPayment?: TrackPayementFunction;
  isFreeDeposit?: boolean;
  isIndirectBooking?: boolean;
  isOrderedProviders?: boolean;
  isBuyNowPayLaterProcessComplete?: boolean;
  getPaymentErrorMessagePath?: (arg0: PaymentErrorMessageParams) => string;
  onBuyNowPayLaterComplete?: () => void;
  sellerOptionRedirect?: (providerId: string) => Promise<void>;
  selectedHouseholdIndex?: number;
  cardFeesPercentage?: number;
  setCardFeesPercentage?: (percentage: number) => void;
};

const defaultPaymentInfoContext: PaymentInfoContextType = {
  isFreeDeposit: false,
  bookingResume: {
    arrivalDate: '',
    departureDate: '',
    name: '',
    transports: [],
    accommodations: { count: 0 },
  },
  paymentRedirectMutation: async () => undefined,
};

export const PaymentInfoContext =
  React.createContext<PaymentInfoContextType>(defaultPaymentInfoContext);

export const PaymentInfoProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: PaymentInfoContextType;
}) => {
  const [isBuyNowPayLaterProcessComplete, setIsBuyNowPayLaterProcessComplete] = useState(false);
  const [cardFeesPercentage, setCardFeesPercentage] = useState(0);
  const paymentInfoContextValue = useMemo(
    () => ({
      ...value,
      isBuyNowPayLaterProcessComplete,
      onBuyNowPayLaterComplete: () => setIsBuyNowPayLaterProcessComplete(true),
      cardFeesPercentage,
      setCardFeesPercentage,
    }),
    [value, isBuyNowPayLaterProcessComplete, cardFeesPercentage],
  );
  return (
    <PaymentInfoContext.Provider value={paymentInfoContextValue}>
      {children}
    </PaymentInfoContext.Provider>
  );
};
